<template>
  <div :class="['analytics-chart', chartType, { 'loading': isLoading }]">
    <h3 v-if="title" class="chart-title">{{ title }}</h3>
    
    <!-- Loading state -->
    <div v-if="isLoading" class="chart-loading">
      <div v-for="n in 8" :key="n" class="loading-bar">
        <div class="loading-shimmer"></div>
      </div>
    </div>
    
    <!-- Bar Chart -->
    <div v-else-if="chartType === 'bar'" class="chart-container">
      <div class="bar-chart">
        <div v-for="(item, index) in data" :key="index" class="chart-bar">
          <div 
            class="bar" 
            :class="{ 'negative': parseFloat(getValue(item)) < 0 }"
            :style="{ height: `${calculateHeight(item)}px` }"
          >
            <span class="value-overlay" :class="{ 'negative': parseFloat(getValue(item)) < 0 }">
              {{ formatValue(getValue(item)) }}
            </span>
          </div>
          <span class="label">{{ getLabel(item) }}</span>
        </div>
      </div>
    </div>
    
    <!-- Line Chart -->
    <div v-else-if="chartType === 'line'" class="chart-container">
      <div class="line-chart">
        <svg :width="lineChartWidth" :height="lineChartHeight">
          <g>
            <!-- Y axis grid lines -->
            <line
              v-for="(y, index) in yAxisTicks"
              :key="`grid-${index}`"
              x1="0"
              :x2="lineChartWidth - 40"
              :y1="yScale(y)"
              :y2="yScale(y)"
              stroke="#e5e7eb"
              stroke-width="1"
            />
            
            <!-- Y axis labels -->
            <text
              v-for="(y, index) in yAxisTicks"
              :key="`label-${index}`"
              :x="lineChartWidth - 35"
              :y="yScale(y) + 4"
              font-size="10"
              text-anchor="end"
              fill="#64748b"
            >{{ formatValue(y) }}</text>
            
            <!-- Line -->
            <path
              :d="linePath"
              fill="none"
              stroke="#3b82f6"
              stroke-width="2"
            />
            
            <!-- Data points -->
            <circle
              v-for="(item, index) in data"
              :key="index"
              :cx="xScale(index)"
              :cy="yScale(getValue(item))"
              r="4"
              fill="#3b82f6"
            />
          </g>
        </svg>
        
        <!-- X axis labels -->
        <div class="x-labels">
          <span 
            v-for="(item, index) in data" 
            :key="index" 
            class="x-label"
            :style="{ left: `${xScale(index)}px` }"
          >
            {{ getLabel(item) }}
          </span>
        </div>
      </div>
    </div>
    
    <!-- Pie Chart -->
    <div v-else-if="chartType === 'pie'" class="chart-container">
      <div class="pie-chart">
        <svg :width="pieChartSize" :height="pieChartSize" viewBox="-1 -1 2 2">
          <g>
            <path
              v-for="(section, index) in pieData"
              :key="index"
              :d="section.path"
              :fill="getColorFromScale(index)"
            />
          </g>
        </svg>
        <div class="pie-legend">
          <div v-for="(item, index) in data" :key="index" class="legend-item">
            <span class="color-indicator" :style="{ backgroundColor: getColorFromScale(index) }"></span>
            <span class="legend-label">{{ getLabel(item) }}</span>
            <span class="legend-value">{{ formatValue(getValue(item)) }}</span>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Horizontal Bar Chart -->
    <div v-else-if="chartType === 'horizontal-bar'" class="chart-container">
      <div class="horizontal-bar-chart">
        <div v-for="(item, index) in data" :key="index" class="horizontal-bar">
          <span class="h-label">{{ getLabel(item) }}</span>
          <div class="h-bar-container">
            <div 
              class="h-bar" 
              :style="{ width: `${calculateWidth(item)}%` }"
              :class="{ 'negative': parseFloat(getValue(item)) < 0 }"
            ></div>
            <span class="h-value">{{ formatValue(getValue(item)) }}</span>
          </div>
        </div>
      </div>
    </div>
    
    <div v-if="!isLoading && caption" class="chart-caption">
      {{ caption }}
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';

export default {
  name: 'AnalyticsChart',
  props: {
    title: {
      type: String,
      default: ''
    },
    caption: {
      type: String,
      default: ''
    },
    data: {
      type: Array,
      required: true
    },
    chartType: {
      type: String,
      default: 'bar',
      validator: (value) => ['bar', 'line', 'pie', 'horizontal-bar'].includes(value)
    },
    valueKey: {
      type: String,
      default: 'value'
    },
    labelKey: {
      type: String,
      default: 'label'
    },
    maxValue: {
      type: Number,
      default: null
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    valuePrefix: {
      type: String,
      default: ''
    },
    valueSuffix: {
      type: String,
      default: ''
    },
    dataFormatter: {
      type: Function,
      default: null
    }
  },
  setup(props) {
    // Helper to get value from item
    const getValue = (item) => {
      return typeof item === 'object' ? item[props.valueKey] : item;
    };
    
    // Helper to get label from item
    const getLabel = (item) => {
      return typeof item === 'object' ? item[props.labelKey] : '';
    };
    
    // Calculate max value for scaling
    const maxDataValue = computed(() => {
      if (props.maxValue !== null) return props.maxValue;
      
      if (!props.data || props.data.length === 0) return 100;
      
      return Math.max(10, ...props.data.map(item => 
        Math.abs(parseFloat(getValue(item)) || 0)
      ));
    });
    
    // Calculate bar height
    const calculateHeight = (item) => {
      const value = parseFloat(getValue(item)) || 0;
      return (Math.abs(value) / maxDataValue.value) * 200;
    };
    
    // Calculate horizontal bar width
    const calculateWidth = (item) => {
      const value = parseFloat(getValue(item)) || 0;
      return (Math.abs(value) / maxDataValue.value) * 100;
    };
    
    // Format display value
    const formatValue = (value) => {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return value;
      
      let formatted = numValue;
      
      // Use custom formatter if provided
      if (props.dataFormatter) {
        formatted = props.dataFormatter(numValue);
      } else {
        // Default formatter
        if (Math.abs(numValue) >= 1000000) {
          formatted = (numValue / 1000000).toFixed(1) + 'M';
        } else if (Math.abs(numValue) >= 1000) {
          formatted = (numValue / 1000).toFixed(1) + 'K';
        } else if (Math.abs(numValue) < 1 && numValue !== 0) {
          formatted = numValue.toFixed(2);
        } else {
          formatted = numValue.toLocaleString();
        }
      }
      
      return props.valuePrefix + formatted + props.valueSuffix;
    };
    
    // Line chart dimensions
    const lineChartWidth = 600;
    const lineChartHeight = 300;
    
    // X and Y scales for line chart
    const xScale = (index) => (index / (props.data.length - 1 || 1)) * (lineChartWidth - 40);
    
    const yScale = (value) => {
      const padding = 20;
      const chartHeight = lineChartHeight - (2 * padding);
      const normalizedValue = parseFloat(value) || 0;
      
      return lineChartHeight - padding - (normalizedValue / maxDataValue.value) * chartHeight;
    };
    
    // Generate Y axis tick values
    const yAxisTicks = computed(() => {
      const tickCount = 5;
      const ticks = [];
      
      for (let i = 0; i <= tickCount; i++) {
        ticks.push((maxDataValue.value / tickCount) * i);
      }
      
      return ticks;
    });
    
    // Line chart path
    const linePath = computed(() => {
      if (!props.data || props.data.length === 0) return '';
      
      let path = '';
      
      props.data.forEach((item, index) => {
        const x = xScale(index);
        const y = yScale(getValue(item));
        
        if (index === 0) {
          path += `M ${x} ${y}`;
        } else {
          path += ` L ${x} ${y}`;
        }
      });
      
      return path;
    });
    
    // Pie chart
    const pieChartSize = 240;
    
    // Generate pie chart data
    const pieData = computed(() => {
      if (!props.data || props.data.length === 0) return [];
      
      const total = props.data.reduce((sum, item) => sum + (parseFloat(getValue(item)) || 0), 0);
      let currentAngle = 0;
      
      return props.data.map((item) => {
        const value = parseFloat(getValue(item)) || 0;
        const angle = (value / total) * Math.PI * 2;
        
        const startAngle = currentAngle;
        const endAngle = currentAngle + angle;
        currentAngle = endAngle;
        
        const startX = Math.sin(startAngle);
        const startY = -Math.cos(startAngle);
        const endX = Math.sin(endAngle);
        const endY = -Math.cos(endAngle);
        
        const largeArcFlag = angle > Math.PI ? 1 : 0;
        
        const path = `M 0 0 L ${startX} ${startY} A 1 1 0 ${largeArcFlag} 1 ${endX} ${endY} Z`;
        
        return {
          path,
          value,
          percentage: (value / total) * 100
        };
      });
    });
    
    // Color scale for pie chart
    const colorScale = [
      '#3b82f6', '#f97316', '#10b981', '#f59e0b', 
      '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6',
      '#6366f1', '#84cc16', '#ef4444', '#64748b'
    ];
    
    const getColorFromScale = (index) => {
      return colorScale[index % colorScale.length];
    };
    
    return {
      getValue,
      getLabel,
      calculateHeight,
      calculateWidth,
      formatValue,
      maxDataValue,
      lineChartWidth,
      lineChartHeight,
      xScale,
      yScale,
      yAxisTicks,
      linePath,
      pieChartSize,
      pieData,
      getColorFromScale
    };
  }
};
</script>

<style scoped>
.analytics-chart {
  position: relative;
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;
  overflow: hidden;
}

.chart-title {
  margin-top: 0;
  margin-bottom: 1.5rem;
  color: #1e293b;
  font-size: 1.1rem;
  font-weight: 600;
}

.chart-caption {
  margin-top: 1rem;
  color: #64748b;
  font-size: 0.85rem;
  text-align: center;
}

.chart-container {
  position: relative;
}

/* Bar chart styles */
.bar-chart {
  display: flex;
  align-items: flex-end;
  gap: 1rem;
  height: 250px;
  padding-bottom: 2rem;
  overflow-x: auto;
}

.chart-bar {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 40px;
}

.bar {
  width: 40px;
  background: #3b82f6;
  border-radius: 4px;
  transition: height 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.bar.negative {
  background: #ef4444;
}

.value-overlay {
  position: absolute;
  top: -25px;
  left: 50%;
  transform: translateX(-50%);
  background: #1e293b;
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  white-space: nowrap;
}

.value-overlay.negative {
  background: #dc2626;
}

.label {
  margin-top: 0.5rem;
  font-size: 0.8rem;
  color: #64748b;
  white-space: nowrap;
}

/* Line chart styles */
.line-chart {
  position: relative;
  height: 350px;
  overflow-x: auto;
}

.x-labels {
  position: relative;
  height: 30px;
  margin-top: 5px;
}

.x-label {
  position: absolute;
  transform: translateX(-50%) rotate(-45deg);
  font-size: 0.8rem;
  color: #64748b;
  transform-origin: top center;
  white-space: nowrap;
}

/* Pie chart styles */
.pie-chart {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 2rem;
  padding: 1rem;
}

.pie-legend {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-width: 300px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
}

.color-indicator {
  width: 16px;
  height: 16px;
  border-radius: 4px;
}

.legend-label {
  flex: 1;
  color: #1e293b;
}

.legend-value {
  font-weight: 500;
  color: #64748b;
}

/* Horizontal bar chart styles */
.horizontal-bar-chart {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 0;
}

.horizontal-bar {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.h-label {
  width: 150px;
  text-align: right;
  font-size: 0.9rem;
  color: #64748b;
  white-space: nowrap;
}

.h-bar-container {
  flex: 1;
  position: relative;
  height: 30px;
  display: flex;
  align-items: center;
}

.h-bar {
  height: 12px;
  background: #3b82f6;
  border-radius: 6px;
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.h-bar.negative {
  background: #ef4444;
}

.h-value {
  margin-left: 0.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: #1e293b;
}

/* Loading state */
.chart-loading {
  display: flex;
  align-items: flex-end;
  gap: 1rem;
  height: 250px;
  padding-bottom: 2rem;
  justify-content: center;
}

.loading-bar {
  width: 40px;
  height: 150px;
  background: #f1f5f9;
  border-radius: 4px;
  position: relative;
  overflow: hidden;
}

.loading-shimmer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.6) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: shimmer 1.5s infinite;
  transform: translateX(-100%);
}

.analytics-chart.loading::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.4);
}

@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}

@media (max-width: 768px) {
  .bar-chart {
    padding-bottom: 3rem;
  }
  
  .bar {
    width: 30px;
  }
  
  .chart-bar {
    min-width: 30px;
  }
  
  .pie-chart {
    flex-direction: column;
  }
  
  .horizontal-bar {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .h-label {
    width: 100%;
    text-align: left;
  }
  
  .h-bar-container {
    width: 100%;
  }
}
</style> 